import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Autocomplete,
  IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { paymentService, customerService } from '../../services';

// Payment methods
const paymentMethods = [
  { value: 'bankTransfer', label: 'Bank Transfer' },
  { value: 'interac', label: 'Interac e-Transfer (Canada)' },
  { value: 'wireTransfer', label: 'International Wire Transfer' },
  { value: 'creditCard', label: 'Credit Card' }
];

// Payment statuses
const paymentStatuses = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' }
];

// Initial payment form state
const initialPaymentState = {
  customerId: '',
  paymentMethod: '',
  amount: '',
  currency: 'CAD',
  referenceNumber: '',
  transferDate: '',
  transferReference: '',
  status: 'pending',
  notes: ''
};

const PaymentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewPayment = !id || id === 'new';
  
  // Form state
  const [formData, setFormData] = useState(initialPaymentState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // Customer selection state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  
  // Fetch customers for dropdown
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await customerService.getCustomers({
          limit: 100  // Get up to 100 customers for the dropdown
        });
        setCustomers(response.customers || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
      } finally {
        setCustomersLoading(false);
      }
    };
    
    fetchCustomers();
  }, []);
  
  // Fetch payment data if editing existing payment
  useEffect(() => {
    if (isNewPayment) return;
    
    const fetchPayment = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const paymentData = await paymentService.getPaymentById(id);
        setFormData(paymentData);
        
        // Find and set the selected customer
        if (paymentData.customerId) {
          const customer = await customerService.getCustomerById(paymentData.customerId);
          setSelectedCustomer(customer);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch payment details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayment();
  }, [id, isNewPayment]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when field is updated
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Handle customer selection
  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
    
    if (newValue) {
      setFormData((prev) => ({
        ...prev,
        customerId: newValue._id,
        // Set currency based on customer country
        currency: newValue.country?.toLowerCase() === 'canada' ? 'CAD' :
                  newValue.country?.toLowerCase() === 'usa' ? 'USD' :
                  'USD'
      }));
      
      // Clear validation error
      if (formErrors.customerId) {
        setFormErrors((prev) => ({
          ...prev,
          customerId: undefined
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        customerId: ''
      }));
    }
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.customerId) {
      errors.customerId = 'Customer is required';
    }
    
    if (!formData.paymentMethod) {
      errors.paymentMethod = 'Payment method is required';
    }
    
    if (!formData.amount) {
      errors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be a positive number';
    }
    
    if (!formData.currency) {
      errors.currency = 'Currency is required';
    }
    
    if (!formData.status) {
      errors.status = 'Status is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Format data for API
      const paymentData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      // Generate a reference number if not provided for new payments
      if (isNewPayment && !paymentData.referenceNumber) {
        // Generate unique parts for the reference number
        // Current timestamp for payment part
        const timestampPart = new Date().getTime().toString().slice(-6);
        
        // For customer part, use the last 6 characters of MongoDB ID if available
        let customerIdPart;
        if (selectedCustomer?._id) {
          // Get last 6 chars of MongoDB ID (which contain the random counter)
          customerIdPart = selectedCustomer._id.toString().substring(18, 24);
        } else {
          // Generate a random 6-character hex string if no customer selected
          customerIdPart = Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
        }
        
        // Combine them into the reference number format
        paymentData.referenceNumber = `CRTS-OTN-${customerIdPart}-${timestampPart}`.toUpperCase();
      }
      
      if (isNewPayment) {
        // Create new payment
        await paymentService.createPayment(paymentData);
      } else {
        // Update existing payment
        await paymentService.updatePayment(id, paymentData);
      }
      
      // Navigate back to payment list or detail
      navigate(isNewPayment ? '/admin/payments' : `/admin/payments/${id}`);
    } catch (err) {
      setError(err.message || `Failed to ${isNewPayment ? 'create' : 'update'} payment`);
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/admin" style={{ textDecoration: 'none', color: 'inherit' }}>
          Dashboard
        </Link>
        <Link to="/admin/payments" style={{ textDecoration: 'none', color: 'inherit' }}>
          Payments
        </Link>
        <Typography color="text.primary">
          {isNewPayment ? 'New Payment' : `Edit Payment`}
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 2 }}
            onClick={() => navigate(isNewPayment ? '/admin/payments' : `/admin/payments/${id}`)}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isNewPayment ? 'Record New Payment' : `Edit Payment`}
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>
      
      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Payment form */}
      <Paper>
        <form onSubmit={handleSubmit}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  id="customer-select"
                  options={customers}
                  loading={customersLoading}
                  getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`.trim() || ''}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      required
                      error={!!formErrors.customerId}
                      helperText={formErrors.customerId}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  select
                  label="Payment Method"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  error={!!formErrors.paymentMethod}
                  helperText={formErrors.paymentMethod}
                >
                  <MenuItem value="">Select a payment method</MenuItem>
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  select
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  error={!!formErrors.status}
                  helperText={formErrors.status}
                >
                  {paymentStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={formData.amount}
                  onChange={handleChange}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  select
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  error={!!formErrors.currency}
                  helperText={formErrors.currency}
                >
                  <MenuItem value="CAD">CAD (Canadian Dollar)</MenuItem>
                  <MenuItem value="USD">USD (US Dollar)</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Transfer Date"
                  name="transferDate"
                  type="date"
                  value={formData.transferDate ? formData.transferDate.split('T')[0] : ''}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment ID/Reference Number"
                  name="referenceNumber"
                  value={formData.referenceNumber || ''}
                  onChange={handleChange}
                  placeholder="System reference number (e.g., CRTS-OTN-123456-ABCDEF)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Reference"
                  name="transferReference"
                  value={formData.transferReference || ''}
                  onChange={handleChange}
                  placeholder="Bank transfer reference, confirmation number, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes || ''}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Add any additional information about this payment"
                />
              </Grid>
            </Grid>
            
            {/* Form actions */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                sx={{ mr: 2 }}
                onClick={() => navigate(isNewPayment ? '/admin/payments' : `/admin/payments/${id}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Save Payment'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default PaymentEdit;